import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { Save } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { RichTextEditor } from './RichTextEditor';
import { useCreateBlogPost, useUpdateBlogPost } from '@/hooks/useBlog';
import { useTranslation } from '@/features/i18n';
import { generateSlug, getReadingTime } from '@/lib/utils';
import type { BlogPost } from '@/types/database';

type BlogPostFormData = {
  title: string;
  slug: string;
  excerpt?: string;
  category?: string;
  cover_image_url?: string;
  seo_title?: string;
  seo_description?: string;
  seo_keywords?: string;
  status: 'draft' | 'published';
};

interface BlogPostEditorProps {
  post?: BlogPost | null;
  onClose: () => void;
}

export function BlogPostEditor({ post, onClose }: BlogPostEditorProps) {
  const [content, setContent] = useState(post?.content_html || '');
  const createPost = useCreateBlogPost();
  const updatePost = useUpdateBlogPost();
  const { t } = useTranslation();

  const blogPostSchema = z.object({
    title: z.string().min(1, t.admin.editor.titleRequired),
    slug: z.string().min(1, t.admin.editor.slugRequired),
    excerpt: z.string().optional(),
    category: z.string().optional(),
    cover_image_url: z.string().url().optional().or(z.literal('')),
    seo_title: z.string().optional(),
    seo_description: z.string().optional(),
    seo_keywords: z.string().optional(),
    status: z.enum(['draft', 'published']),
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<BlogPostFormData>({
    resolver: zodResolver(blogPostSchema),
    defaultValues: {
      title: post?.title || '',
      slug: post?.slug || '',
      excerpt: post?.excerpt || '',
      category: post?.category || '',
      cover_image_url: post?.cover_image_url || '',
      seo_title: post?.seo_title || '',
      seo_description: post?.seo_description || '',
      seo_keywords: post?.seo_keywords || '',
      status: post?.status || 'draft',
    },
  });

  // title is auto-generated for slug when creating new posts

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setValue('title', newTitle);
    if (!post) {
      setValue('slug', generateSlug(newTitle));
    }
  };

  const onSubmit = async (data: BlogPostFormData) => {
    const postData = {
      ...data,
      content_html: content,
      reading_time: getReadingTime(content),
      published_at: data.status === 'published' ? new Date().toISOString() : null,
      tags: [],
      author_name: null,
      author_id: null,
      og_image_url: data.cover_image_url || null,
    };

    if (post) {
      await updatePost.mutateAsync({ id: post.id, ...postData });
    } else {
      await createPost.mutateAsync(postData as Parameters<typeof createPost.mutateAsync>[0]);
    }
    onClose();
  };

  const categoryOptions = [
    { value: '', label: t.admin.editor.selectCategory },
    { value: 'CRM', label: t.admin.editor.categories.crm },
    { value: 'Teknologi', label: t.admin.editor.categories.technology },
    { value: 'Markedsføring', label: t.admin.editor.categories.marketing },
    { value: 'Design', label: t.admin.editor.categories.design },
    { value: 'Tips', label: t.admin.editor.categories.tips },
  ];

  const statusOptions = [
    { value: 'draft', label: t.admin.common.draft },
    { value: 'published', label: t.admin.common.published },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Basic info */}
          <div className="grid md:grid-cols-2 gap-6">
            <Input
              label={`${t.admin.editor.title} *`}
              error={errors.title?.message}
              {...register('title')}
              onChange={handleTitleChange}
            />
            <Input
              label={`${t.admin.editor.slug} *`}
              error={errors.slug?.message}
              {...register('slug')}
            />
          </div>

          <Textarea
            label={t.admin.editor.excerpt}
            hint={t.admin.editor.excerptHint}
            rows={3}
            {...register('excerpt')}
          />

          <div className="grid md:grid-cols-2 gap-6">
            <Select
              label={t.admin.editor.category}
              options={categoryOptions}
              {...register('category')}
            />
            <Input
              label={t.admin.editor.coverImageUrl}
              placeholder="https://..."
              {...register('cover_image_url')}
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              {t.admin.editor.content}
            </label>
            <RichTextEditor
              content={content}
              onChange={setContent}
              placeholder={t.admin.editor.contentPlaceholder}
            />
          </div>

          {/* SEO */}
          <div className="pt-6 border-t border-neutral-200 dark:border-neutral-700">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              {t.admin.editor.seoSettings}
            </h3>
            <div className="space-y-4">
              <Input
                label={t.admin.editor.seoTitle}
                hint={t.admin.editor.seoTitleHint}
                {...register('seo_title')}
              />
              <Textarea
                label={t.admin.editor.seoDescription}
                hint={t.admin.editor.seoDescriptionHint}
                rows={2}
                {...register('seo_description')}
              />
              <Input
                label={t.admin.editor.keywords}
                hint={t.admin.editor.keywordsHint}
                {...register('seo_keywords')}
              />
            </div>
          </div>

          {/* Status */}
          <div className="pt-6 border-t border-neutral-200 dark:border-neutral-700">
            <Select
              label={t.admin.editor.status}
              options={statusOptions}
              {...register('status')}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between p-6 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800">
        <Button type="button" variant="outline" onClick={onClose}>
          {t.admin.editor.cancel}
        </Button>
        <Button
          type="submit"
          isLoading={isSubmitting}
          leftIcon={<Save className="w-4 h-4" />}
        >
          {post ? t.admin.editor.saveChanges : t.admin.editor.createPost}
        </Button>
      </div>
    </form>
  );
}
